import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n.js';
import { useGameState } from '../../net/GameProvider.js';
import type { StatusNoticeTone } from '../StatusNotice.js';

export function usePaymentRefresh() {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const [request, setRequest] = useState<{ invoiceId: string; previousHistory: typeof state.billingHistory } | null>(null);
  const [notice, setNotice] = useState<{ message: string; tone: StatusNoticeTone } | null>(null);

  useEffect(() => {
    if (!request || state.billingHistory === request.previousHistory) return;
    const payment = state.billingHistory?.find((entry) => entry.invoice_id === request.invoiceId);
    setNotice(payment?.status === 'paid'
      ? { tone: 'success', message: lang === 'mn' ? 'Төлбөр баталгаажлаа.' : 'Payment confirmed.' }
      : payment
        ? { tone: 'pending', message: lang === 'mn' ? 'Төлбөр хараахан баталгаажаагүй байна. Төлөөгүй бол QR кодыг уншуулна уу. Төлсөн бол түр хүлээгээд дахин шалгана уу.' : 'Payment not confirmed yet. Scan the QR code to pay, or check again shortly if you already paid.' }
        : { tone: 'error', message: lang === 'mn' ? 'Энэ серверт төлбөрийн бүртгэл олдсонгүй. Тусламжтай холбогдоно уу.' : 'This server has no record of the invoice. Contact support.' });
    setRequest(null);
  }, [request, state.billingHistory, lang]);

  useEffect(() => {
    if (!request) return;
    if (state.connectionStatus !== 'connected') {
      setNotice({ tone: 'error', message: lang === 'mn' ? 'Сервертэй холбогдоогүй. Төлөв шалгах боломжгүй.' : 'Disconnected from server. Cannot refresh payment status.' });
      setRequest(null);
      return;
    }
    const timeout = window.setTimeout(() => {
      setNotice({ tone: 'error', message: lang === 'mn' ? 'Сервер хариу өгсөнгүй. Дараа дахин шалгана уу.' : 'The server did not respond. Try again later.' });
      setRequest(null);
    }, 10000);
    return () => window.clearTimeout(timeout);
  }, [request, state.connectionStatus, lang]);

  const refreshPayment = (invoiceId: string) => {
    if (state.connectionStatus !== 'connected') return;
    setNotice(null);
    setRequest({ invoiceId, previousHistory: state.billingHistory });
    actions.checkPayment(invoiceId);
  };

  return { refreshPayment, refreshRequest: request, refreshNotice: notice };
}
